<template>
  <div class="user-coupon-list">
    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <h2>领取列表</h2>
        </div>
      </template>
      
      <!-- 搜索和筛选 -->
      <el-form :inline="true" :model="searchForm" class="search-form">
        <el-form-item label="状态">
          <el-select v-model="searchForm.status" placeholder="请选择状态" clearable style="width: 150px;">
            <el-option label="未使用" value="unused" />
            <el-option label="已使用" value="used" />
          </el-select>
        </el-form-item>
        <el-form-item label="券列表">
          <el-select v-model="searchForm.couponId" placeholder="请选择卡券" clearable style="width: 250px;">
            <el-option 
              v-for="coupon in couponsList" 
              :key="coupon.id" 
              :label="coupon.title" 
              :value="coupon.id" 
            />
          </el-select>
        </el-form-item>
        <el-form-item label="券码">
          <el-input v-model="searchForm.code" placeholder="请输入券码" clearable style="width: 200px;"></el-input>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="searchUserCoupons">搜索</el-button>
          <el-button @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>
      
      <!-- 用户卡券列表 -->
      <el-table :data="userCoupons" v-loading="loading" style="width: 100%">
        <el-table-column prop="code" label="券码" width="120" />
        <el-table-column label="用户信息" width="150">
          <template #default="scope">
            <div class="user-info">
              <span>{{ scope.row.user?.nickname || scope.row.user?.name || '未知用户' }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="卡券信息" width="250">
          <template #default="scope">
            <div class="coupon-info">
              <div class="coupon-title">{{ scope.row.coupon?.title || '未知券' }}</div>
              <div class="coupon-meta">
                <div class="category-with-badge">
                  <el-tag size="small">{{ this.getCategoryName(scope.row.coupon?.category) }}</el-tag>
                  <el-tag size="small" :type="scope.row.coupon?.type === 'virtual' ? 'success' : 'info'" class="type-badge">
                    {{ scope.row.coupon?.type === 'virtual' ? '虚拟' : '实物' }}
                  </el-tag>
                </div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="received_at" label="领取时间" width="180" />
        <el-table-column prop="expired_at" label="过期时间" width="180" />
        <el-table-column prop="status" label="状态" width="100">
          <template #default="scope">
            <el-tag type="success" v-if="scope.row.status === 'unused'">未使用</el-tag>
            <el-tag type="warning" v-else-if="scope.row.status === 'used'">已使用</el-tag>
            <el-tag type="danger" v-else>已过期</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="used_at" label="使用时间" width="180" />
        <el-table-column label="操作" width="150" fixed="right">
          <template #default="scope">
            <el-button type="primary" size="small" @click="handleViewDetail(scope.row)">详情</el-button>
            <el-button v-if="scope.row.status === 'unused'" type="success" size="small" @click="handleUseCoupon(scope.row.id)">使用</el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          v-model:current-page="pagination.page"
          v-model:page-size="pagination.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>
    
    <!-- 用户卡券详情抽屉 -->
    <el-drawer
      v-model="detailDrawerVisible"
      title="用户卡券详情"
      size="60%"
      direction="rtl"
      @close="handleDetailDrawerClose"
    >
      <div class="user-coupon-detail">
        <el-descriptions title="基本信息" :column="2" border>
          <el-descriptions-item label="券码">{{ currentUserCoupon.code }}</el-descriptions-item>
          <el-descriptions-item label="用户">{{ currentUserCoupon.user?.nickname || currentUserCoupon.user?.name || '未知用户' }}</el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag type="success" v-if="currentUserCoupon.status === 'unused'">未使用</el-tag>
            <el-tag type="warning" v-else-if="currentUserCoupon.status === 'used'">已使用</el-tag>
            <el-tag type="danger" v-else>已过期</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="领取时间">{{ currentUserCoupon.received_at }}</el-descriptions-item>
          <el-descriptions-item label="过期时间">{{ currentUserCoupon.expired_at }}</el-descriptions-item>
          <el-descriptions-item label="使用时间" :span="2">{{ currentUserCoupon.used_at || '未使用' }}</el-descriptions-item>
        </el-descriptions>
        
        <el-divider content-position="left">卡券信息</el-divider>
        
        <el-descriptions title="卡券详情" :column="1" border>
          <el-descriptions-item label="卡券标题">{{ currentUserCoupon.coupon?.title }}</el-descriptions-item>
          <el-descriptions-item label="卡券副标题">{{ currentUserCoupon.coupon?.subtitle || '-' }}</el-descriptions-item>
          <el-descriptions-item label="券分类">
            <div class="category-with-badge">
              {{ this.getCategoryName(currentUserCoupon.coupon?.category) }}
              <el-tag size="small" :type="currentUserCoupon.coupon?.type === 'virtual' ? 'success' : 'info'" class="type-badge">
                {{ currentUserCoupon.coupon?.type === 'virtual' ? '虚拟' : '实物' }}
              </el-tag>
            </div>
          </el-descriptions-item>
          <el-descriptions-item label="有效期(天)">{{ currentUserCoupon.coupon?.valid_days }}</el-descriptions-item>
          <el-descriptions-item label="卡券描述" :span="1" v-html="currentUserCoupon.coupon?.description"></el-descriptions-item>
        </el-descriptions>
        
        <!-- 操作按钮 -->
        <div class="detail-actions">
          <el-button type="primary" @click="detailDrawerVisible = false">关闭</el-button>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import couponApi from '../../api/coupon.js';

export default {
  name: 'UserCouponList',
  setup() {
    // 用户卡券数据
    const userCoupons = ref([]);
    // 加载状态
    const loading = ref(false);
    // 查询条件
    const searchForm = reactive({
      couponId: '',
      status: '',
      code: '',
    });
    
    // 卡券列表（用于筛选）
    const couponsList = ref([]);
    // 分页信息
    const pagination = reactive({
      page: 1,
      pageSize: 10,
      total: 0,
    });
    
    // 详情抽屉
    const detailDrawerVisible = ref(false);
    const currentUserCoupon = ref({});
    
    // 券分类名称映射
    const categoryNames = {
      authentication: '鉴别服务券',
      maintenance: '保养券',
      pre_order: '新品预定权益',
      activity_invitation: '活动邀约权益',
      home_recycle: '上门回收券',
      repair: '维修券',
      peripheral_exchange: '周边兑换券',
      gift_exchange: '赠品兑换券',
    };

    // 获取分类中文名称
    const getCategoryName = (category) => {
      return category ? (categoryNames[category] || category) : '';
    };

    // 加载卡券列表（用于筛选）
    const loadCouponsList = () => {
      couponApi.getCoupons({ page: 1, per_page: 100 })
        .then(response => {
          couponsList.value = response.data || [];
        })
        .catch(error => {
          console.error('获取卡券列表失败：', error);
          ElMessage.error('获取卡券列表失败：' + (error.message || '网络错误'));
        });
    };
    
    // 加载用户卡券列表
    const loadUserCoupons = () => {
      loading.value = true;
      
      // 构建请求参数
      const params = {
        coupon_id: searchForm.couponId,
        status: searchForm.status,
        code: searchForm.code,
        page: pagination.page,
        per_page: pagination.pageSize,
      };
      
      // 调用API获取用户卡券列表
      couponApi.getUserCoupons(params)
        .then(response => {
          userCoupons.value = response.data || [];
          pagination.total = response.total || 0;
        })
        .catch(error => {
          ElMessage.error('获取用户卡券列表失败：' + (error.message || '网络错误'));
          console.error('获取用户卡券列表失败：', error);
        })
        .finally(() => {
          loading.value = false;
        });
    };

    // 搜索用户卡券
    const searchUserCoupons = () => {
      pagination.page = 1;
      loadUserCoupons();
    };

    // 重置搜索条件
    const resetSearch = () => {
      searchForm.couponId = '';
      searchForm.status = '';
      searchForm.code = '';
      searchUserCoupons();
    };

    // 切换页码
    const handleCurrentChange = (page) => {
      pagination.page = page;
      loadUserCoupons();
    };

    // 切换每页条数
    const handleSizeChange = (pageSize) => {
      pagination.pageSize = pageSize;
      pagination.page = 1;
      loadUserCoupons();
    };
    
    // 查看详情
    const handleViewDetail = (row) => {
      currentUserCoupon.value = row;
      detailDrawerVisible.value = true;
    };
    
    // 使用卡券
    const handleUseCoupon = (id) => {
      ElMessageBox.confirm('确定要标记这个卡券为已使用吗？', '使用确认', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
      .then(() => {
        // 调用使用卡券API
        couponApi.updateUserCoupon(id, { status: 'used', used_at: new Date().toISOString() })
          .then(() => {
            ElMessage.success('卡券已标记为已使用');
            // 重新加载数据
            loadUserCoupons();
          })
          .catch(error => {
            ElMessage.error('使用卡券失败：' + (error.message || '网络错误'));
            console.error('使用卡券失败：', error);
          });
      })
      .catch(() => {
        // 取消使用
      });
    };
    
    // 关闭详情抽屉
    const handleDetailDrawerClose = () => {
      currentUserCoupon.value = {};
      detailDrawerVisible.value = false;
    };

    // 初始化数据
    onMounted(() => {
      loadCouponsList();
      loadUserCoupons();
    });

    return {
      userCoupons,
      loading,
      searchForm,
      pagination,
      couponsList,
      detailDrawerVisible,
      currentUserCoupon,
      getCategoryName,
      loadUserCoupons,
      searchUserCoupons,
      resetSearch,
      handleCurrentChange,
      handleSizeChange,
      handleViewDetail,
      handleUseCoupon,
      handleDetailDrawerClose,
    };
  },
};
</script>

<style scoped>
.user-coupon-list {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-form {
  margin-bottom: 20px;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.user-info {
  display: flex;
  flex-direction: column;
}

.coupon-info {
  display: flex;
  flex-direction: column;
}

.coupon-title {
  font-weight: bold;
  margin-bottom: 4px;
}

.coupon-subtitle {
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
}

.coupon-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}

.coupon-info .category-with-badge {
  margin: 0;
}

.category-with-badge {
  position: relative;
  display: inline-block;
  padding: 0 4px;
}

.type-badge {
  position: absolute;
  top: -2px;
  right: -8px;
  font-size: 10px;
  height: 14px;
  line-height: 14px;
  padding: 0 5px;
  border-radius: 7px;
  transform: scale(0.85);
  white-space: nowrap;
  z-index: 1;
}

.user-coupon-detail {
  padding: 20px 0;
}

.detail-actions {
  margin-top: 20px;
  text-align: right;
}
</style>
